import { useState, useRef, useCallback, useMemo, ReactNode } from "react";
import { CreateTaskDrawer } from "@components/drawers/CreateTaskDrawer";
import { GlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/GlobalTransactionViewDrawer";
import { ReconciliationModalWrapper } from "@components/reconciliation/ReconciliationModalWrapper";
import { FinancialDocumentDrawer } from "@components/documents/FinancialDocumentDrawer";
import { FinancialDocumentDetailsModalOverlay } from "@components/documents/FinancialDocumentDetails/FinancialDocumentDetailsModalOverlay";
import { TransactionRuleCreationDrawer } from "@components/transactionRules/TransactionRuleCreation/TransactionRuleCreationDrawer";
import { AddTransactionsModalContainer } from "@components/transactions/AddTransactionsModal/AddTransactionsModalContainer";
import {
    RecordCheckModalContainer,
    RecordDepositModalContainer,
} from "@components/checks/RecordCheckModal/RecordCheckModalContainer";
import { ImporterModal } from "@components/importer/ImporterModal";
import { TransactionSplitModalContainer } from "@components/transactions/TransactionSplitV2/TransactionSplitModalContainer";
import { CreateCoAAccountModalContainer } from "@components/accounting/chartOfAccounts/CreateCoAAccountModalContainer";
import { DisableChartOfAccountsModalWrapper } from "@components/accounting/chartOfAccounts/DisableChartOfAccountsModalWrapper";
import { EnableChartOfAccountsModalWrapper } from "@components/accounting/chartOfAccounts/EnableChartOfAccountsModalWrapper";
import { DeleteChartOfAccountsModalWrapper } from "@components/accounting/chartOfAccounts/DeleteChartOfAccountsModalWrapper";
import { MergeChartOfAccountsModalWrapper } from "@components/accounting/chartOfAccounts/MergeChartOfAccountsModalWrapper";
import { ReassignAccountRoleModalWrapper } from "@components/accounting/chartOfAccounts/ReassignAccountRoleModalWrapper";
import { DeleteGroupModalWrapper } from "@components/accounting/chartOfAccounts/ChartOfAccountsGroupModal/DeleteGroupModalWrapper";
import { SimilarTransactionsUpdateDrawer } from "@components/counterparty/SimilarTransactionsUpdateDrawer/SimilarTransactionsUpdateDrawer";
import { ReferralDrawerContainer } from "@components/settings/Referrals/ReferralDrawerContainer";
import { ReportSettingsEditorDrawerContainer } from "@components/settings/ReportSettings/ReportSettingsEditorDrawerContainer";
import { QuickbooksMapAccountsContainer } from "@components/quickbooks/QuickbooksMapAccountsContainer";
import { CoaMappingDrawerContainer } from "@components/accountingMigration/mappingDrawer/CoaMappingDrawerContainer";
import { AccountingMigrationModalContainer } from "@components/accounting/AccountingMigrationModal";
import { JournalEntryDrawerWrapper } from "@components/accounting/journalEntryDrawer/JournalEntryDrawerWrapper";
import { CreateCategoryModalContainer } from "@components/settings/Categories/CreateCategoryModalContainer";
import { UpdateCategoryModalContainer } from "@components/settings/Categories/UpdateCategoryModalContainer";
import { DeleteCategoryModalContainer } from "@components/settings/Categories/DeleteCategoryModalContainer";
import { CreateDocumentCategoryModalContainer } from "@components/settings/DocumentCategories/CreateDocumentCategoryModalContainer";
import { BillLineItemsDrilldownDrawerWrapper } from "@components/bills/BillLineItemsDrilldownDrawer";
import { InvoiceLineItemsDrilldownDrawerWrapper } from "@components/invoices/InvoiceLineItemsDrilldownDrawer";
import { GlobalInvoicesDrilldownDrawer } from "@components/invoices/GlobalInvoicesDrilldownDrawer";
import { GlobalInvoiceTemplatesDrilldownDrawer } from "@components/invoices/templates/GlobalInvoiceTemplatesDrilldownDrawer";
import { GlobalBillsDrilldownDrawer } from "@components/bills/GlobalBillsDrilldownDrawer";
import { MemberManagementAddMemberModalOverlay } from "@components/memberManagement/MemberManagementAddMemberModalOverlay";
import { MemberManagementConfigurePermissionsModalOverlay } from "@components/memberManagement/MemberManagementConfigurePermissionsModalOverlay";
import { MemberManagementConfirmOrganizationsModalOverlay } from "@components/memberManagement/MemberManagementConfirmOrganizationsModalOverlay";
import { EnrollOrganizationModalOverlay } from "@components/organizationView/organizationBilling/EnrollOrganizationModalOverlay";
import { TransactionMatchDrawerWrapper } from "@components/accounting/transactionMatch/TransactionMatchDrawerWrapper";
import { DocumentMatchDrawerWrapper } from "@components/accounting/transactionMatch/DocumentMatchDrawerWrapper";
import { ManualBillCreationDrawerWrapper } from "@components/bills/ManualBillCreationDrawer/ManualBillCreationDrawerWrapper";
import { ManualInvoiceCreationDrawerWrapper } from "@components/invoices/ManualInvoiceCreationDrawer/ManualInvoiceCreationDrawerWrapper";
import { ManualCashItemImportDrawerWrapper } from "@components/cashItemImporter/ManualCashItemImportDrawerWrapper";
import { CounterpartyEditorDrawerWrapper } from "@components/settings/Directory/CounterpartyEditor/CounterpartyEditorDrawerWrapper";
import { NativeInvoiceDrawerWrapper } from "@components/invoices/NativeInvoiceDrawer/NativeInvoiceDrawerWrapper";
import { AccrualDocumentImporterModal } from "@components/accrualDocumentImporter/AccrualDocumentImporterModal";
import { ConsolidationWorkflowContainer } from "@components/ownership/consolidation/ConsolidationWorkflow";
import { AddOrganizationMemberModalOverlay } from "@components/organizationView/team/AddOrganizationMemberModalOverlay";
import { ConfigureOrganizationPermissionsModalOverlay } from "@components/organizationView/team/ConfigureOrganizationPermissionsModalOverlay";
import { PostConnectAssignmentOverlay } from "@components/organizationView/integrations/PostConnectAssignmentOverlay";
import { AddEntityDrawerContainer } from "@components/addEntity/AddEntityDrawerContainer";
import { MultiEntityUpgradeModalContainer } from "@components/addEntity/MultiEntityUpgradeModalContainer";
import { AddClientOverlayContainer } from "@components/organizationView/clients/AddClient/AddClientOverlayContainer";
import { LegacyPlusEntityLimitModalContainer } from "@components/billing/LegacyPlusEntityLimit/LegacyPlusEntityLimitModalContainer";
import { CommandsProvider } from "@components/commands/CommandsProvider";
import { ScheduleLockedPeriodsModalWrapper } from "@components/accounting/schedules/ScheduleLockedPeriodsModalWrapper";
import { SubScopeContext, SubScopeValue } from "@state/subScopeContext.lib";
import {
    OverlaysContext,
    OverlayType,
    OverlayData,
    OverlaysData,
} from "./OverlayContext";

interface Props {
    children: ReactNode;
}

const OVERLAY_CLOSE_ANIMATION_DURATION = 500;

export const OverlaysProvider = ({ children }: Props) => {
    const [currentOverlay, setCurrentOverlay] = useState<OverlaysData | null>(
        null,
    );
    const [overlayIsClosing, setOverlayIsClosing] = useState<boolean>(false);
    const [overlaySubScope, setOverlaySubScope] =
        useState<SubScopeValue | null>(null);

    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const overlayInstanceIdRef = useRef(0);

    const openOverlay = useCallback(
        <T extends OverlayType>(
            type: T,
            data: OverlayData<T>,
            subScope: SubScopeValue | null = null,
        ) => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }

            setOverlaySubScope(subScope);
            overlayInstanceIdRef.current += 1;
            setCurrentOverlay({
                [type]: {
                    data,
                    type,
                    instanceId: overlayInstanceIdRef.current,
                },
            });

            // Hosts render `open={!overlayIsClosing}`. A first paint with
            // closing=true mounts the drawer closed; the existing close
            // timeout then flips it so Base UI can play the enter transition.
            if (currentOverlay !== null && !overlayIsClosing) {
                setOverlayIsClosing(false);
                return;
            }

            setOverlayIsClosing(true);
            closeTimeoutRef.current = setTimeout(() => {
                closeTimeoutRef.current = null;
                setOverlayIsClosing(false);
            }, 0);
        },
        [currentOverlay, overlayIsClosing],
    );

    const closeOverlay = useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        setOverlayIsClosing(true);
        closeTimeoutRef.current = setTimeout(() => {
            setCurrentOverlay(null);
            setOverlaySubScope(null);
            setOverlayIsClosing(false);
            closeTimeoutRef.current = null;
        }, OVERLAY_CLOSE_ANIMATION_DURATION);
    }, []);

    const getOverlayInfo = useCallback(
        <T extends OverlayType>(
            type: T,
        ): { type: T; data?: OverlayData<T>; instanceId: number } | undefined =>
            currentOverlay?.[type],
        [currentOverlay],
    );

    const contextValue = useMemo(
        () => ({ openOverlay, closeOverlay, getOverlayInfo, overlayIsClosing }),
        [openOverlay, closeOverlay, getOverlayInfo, overlayIsClosing],
    );

    return (
        <OverlaysContext.Provider value={contextValue}>
            <CommandsProvider>
                {children}

                <SubScopeContext.Provider value={overlaySubScope}>
                    {/* HERE add your overlay that wants to be global */}
                    <SimilarTransactionsUpdateDrawer />
                    <GlobalTransactionViewDrawer />
                    <CreateTaskDrawer />
                    <ReconciliationModalWrapper />
                    <FinancialDocumentDrawer />
                    <FinancialDocumentDetailsModalOverlay />
                    <TransactionRuleCreationDrawer />
                    <AddTransactionsModalContainer />
                    <RecordCheckModalContainer />
                    <RecordDepositModalContainer />
                    <ImporterModal />
                    <TransactionSplitModalContainer />
                    <CreateCoAAccountModalContainer />
                    <DisableChartOfAccountsModalWrapper />
                    <EnableChartOfAccountsModalWrapper />
                    <DeleteChartOfAccountsModalWrapper />
                    <MergeChartOfAccountsModalWrapper />
                    <ScheduleLockedPeriodsModalWrapper />
                    <ReassignAccountRoleModalWrapper />
                    <DeleteGroupModalWrapper />
                    <ReferralDrawerContainer />
                    <ReportSettingsEditorDrawerContainer />
                    <AccountingMigrationModalContainer />
                    <QuickbooksMapAccountsContainer />
                    <CoaMappingDrawerContainer />
                    <JournalEntryDrawerWrapper />
                    <CreateCategoryModalContainer />
                    <UpdateCategoryModalContainer />
                    <DeleteCategoryModalContainer />
                    <CreateDocumentCategoryModalContainer />
                    <BillLineItemsDrilldownDrawerWrapper />
                    <InvoiceLineItemsDrilldownDrawerWrapper />
                    <GlobalInvoicesDrilldownDrawer />
                    <GlobalInvoiceTemplatesDrilldownDrawer />
                    <GlobalBillsDrilldownDrawer />
                    <MemberManagementAddMemberModalOverlay />
                    <MemberManagementConfigurePermissionsModalOverlay />
                    <MemberManagementConfirmOrganizationsModalOverlay />
                    <EnrollOrganizationModalOverlay />
                    <AddOrganizationMemberModalOverlay />
                    <ConfigureOrganizationPermissionsModalOverlay />
                    <TransactionMatchDrawerWrapper />
                    <DocumentMatchDrawerWrapper />
                    <ManualBillCreationDrawerWrapper />
                    <ManualInvoiceCreationDrawerWrapper />
                    <ManualCashItemImportDrawerWrapper />
                    <NativeInvoiceDrawerWrapper />
                    <CounterpartyEditorDrawerWrapper />
                    <AccrualDocumentImporterModal />
                    <ConsolidationWorkflowContainer />
                    <PostConnectAssignmentOverlay />
                    <AddClientOverlayContainer />
                    <AddEntityDrawerContainer />
                    <LegacyPlusEntityLimitModalContainer />
                    <MultiEntityUpgradeModalContainer />
                </SubScopeContext.Provider>
            </CommandsProvider>
        </OverlaysContext.Provider>
    );
};
